import { Store } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAppSelector } from '@/store/hooks'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessUserQuery } from '@/hooks/business/useBusinessUserQuery'

interface DispatchAreaSelectorProps {
  selectedArea: string
  setSelectedArea: (_value: string) => void
  // Extended props for flexibility
  label?: string
  placeholder?: string
  className?: string
  triggerClassName?: string
  excludeAreaId?: number
  autoSelectFirst?: boolean
  showIcon?: boolean
  disabled?: boolean
  id?: string
}

export const DispatchAreaSelector = ({
  selectedArea,
  setSelectedArea,
  // Default values for backward compatibility
  label = 'Área de almacén',
  placeholder = 'Seleccionar área de almacén',
  className = 'flex flex-col gap-2',
  triggerClassName = 'w-full md:w-[250px]',
  excludeAreaId,
  autoSelectFirst = true,
  showIcon = true,
  disabled = false,
  id = 'dispatch-area',
}: DispatchAreaSelectorProps) => {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  const { user, allowRoles } = useBusinessUserQuery({
    businessId: businessData?.id ?? 0,
    options: {
      enabled: !!businessData?.id,
    },
  })

  const finderAreasFromRedux = useAppSelector(
    (state) => state.nomenclator.finderAreas
  )

  const areas = useMemo(() => {
    if (!finderAreasFromRedux) return []

    let filteredAreas = finderAreasFromRedux

    // Filter by type STOCK
    filteredAreas = filteredAreas.filter((area) => area.type === 'STOCK')

    // Filter by businessId
    if (businessData?.id) {
      filteredAreas = filteredAreas.filter(
        (area) => area.business?.id === businessData.id
      )
    }

    // Filter by user permissions
    if (!allowRoles(['OWNER'], true)) {
      filteredAreas = filteredAreas.filter((area) =>
        user?.allowedStockAreas?.some((item) => item.id === area.id)
      )
    }

    // Exclude specific area if provided
    if (excludeAreaId) {
      filteredAreas = filteredAreas.filter((area) => area.id !== excludeAreaId)
    }

    return filteredAreas
  }, [
    finderAreasFromRedux,
    businessData?.id,
    user?.allowedStockAreas,
    allowRoles,
    excludeAreaId,
  ])

  useEffect(() => {
    if (autoSelectFirst && areas && areas.length > 0 && !selectedArea) {
      setSelectedArea(areas[0].id.toString())
    }
  }, [areas, selectedArea, setSelectedArea, autoSelectFirst])

  return (
    <div className={className}>
      {label && (
        <Label className="text-lg font-medium" htmlFor={id}>
          {label}
        </Label>
      )}
      <Select
        disabled={
          disabled ||
          !areas ||
          areas.length === 0 ||
          isLoadingBusinessData ||
          isErrorBusinessData
        }
        onValueChange={setSelectedArea}
        value={selectedArea}
      >
        <SelectTrigger className={triggerClassName} id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <div className="flex max-h-[200px] flex-col gap-2 overflow-y-auto">
            {areas &&
              areas.length > 0 &&
              areas?.map((pos) => (
                <SelectItem
                  className="cursor-pointer"
                  key={pos.id}
                  value={pos.id.toString()}
                >
                  <div className="flex items-center gap-2">
                    {showIcon && (
                      <Store className="h-4 w-4 text-muted-foreground" />
                    )}
                    {pos.business?.name} - {pos.name}
                  </div>
                </SelectItem>
              ))}
          </div>
        </SelectContent>
      </Select>
    </div>
  )
}
